import { supabase } from './supabase';
import { callMcpTool } from './mcpClient';

/**
 * Frontend service for tool_locations (#119, backend in #113 / migration 059).
 *
 * Mirrors toolCategoryService:
 * - Direct Supabase queries for reads (RLS allows authenticated users to
 *   read active rows; admins additionally see inactive ones).
 * - Admin writes go through the MCP endpoint so server-side audit logging
 *   and validation fire (admin-only at the server).
 */

/**
 * List tool locations.
 * @param {object} opts
 * @param {boolean} [opts.active_only=true] - Hide inactive locations.
 * @param {string} [opts.project_id] - Filter to a single project's locations.
 * @param {string} [opts.parent_id] - Filter to children of a given parent (UUID),
 *   or pass the literal string "null" to fetch only root-level locations.
 * @param {string} [opts.type] - One of: zona, bodega, vehiculo, proyecto, sub, otro.
 * @returns {Promise<{ data: Array, error: Error|null }>}
 */
export const listToolLocations = async ({
  active_only = true,
  project_id,
  parent_id,
  type
} = {}) => {
  try {
    let q = supabase
      .from('tool_locations')
      .select('id, code, label, type, project_id, parent_location_id, is_active, created_at')
      .order('label', { ascending: true });

    if (active_only) q = q.eq('is_active', true);
    if (project_id) q = q.eq('project_id', project_id);
    if (parent_id === 'null') q = q.is('parent_location_id', null);
    else if (parent_id) q = q.eq('parent_location_id', parent_id);
    if (type) q = q.eq('type', type);

    const { data, error } = await q;
    return { data: data || [], error };
  } catch (error) {
    return { data: [], error };
  }
};

/**
 * Create a new tool location (admin-only on the server).
 */
export const createToolLocation = async ({ code, label, type, project_id, parent_location_id }) => {
  const payload = { code, label, type };
  if (project_id) payload.project_id = project_id;
  if (parent_location_id) payload.parent_location_id = parent_location_id;
  return callMcpTool('create_tool_location', payload);
};

/**
 * Update a tool location's label or active flag (admin-only on the server).
 * Only `label` and `is_active` are mutable per backend contract.
 */
export const updateToolLocation = async ({ location_id, label, is_active }) => {
  const payload = { location_id };
  if (label !== undefined) payload.label = label;
  if (is_active !== undefined) payload.is_active = is_active;
  return callMcpTool('update_tool_location', payload);
};
